import { CalendarPlus, ClipboardPlus, LifeBuoy, Plus, UserPlus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useSession } from '../../lib/session/session-context.js';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  BrandLogo,
} from '@tailoring-crm/ui';
import { FollowUpsMenu } from './follow-ups-menu.js';
import { GlobalSearch } from './global-search.js';
import { UserMenu } from './user-menu.js';

/**
 * The bar across the top of every screen: the shop's name, the one search box, the one green
 * "+" that starts anything, help, and who is signed in. It is dark, the sidebar hangs from it,
 * and the page below is white — so the chrome is one frame and the work is what is lit.
 *
 * The shop name sits after a divider in the muted chrome colour, a workspace label rather than a
 * second logo. It stays, and truncates rather than wrapping, because on a shared handset "which
 * shop am I in, and as whom" is the question this bar exists to answer.
 */
export function AppHeader({ onSignOut }: { onSignOut?: () => void }) {
  const { t } = useTranslation();
  const session = useSession();
  const navigate = useNavigate();
  const canCreate = session !== null && session.role !== 'tailor';
  // Separate from `canCreate` although the rule matches today: one asks what this person may
  // start, the other what they are already owed. Tying the bell to the "+" would hide the
  // chasing list the day somebody narrows who is allowed to book an order.
  const hasFollowUps = session !== null && session.role !== 'tailor';

  return (
    <header className="fixed inset-x-0 top-0 z-40 flex h-header items-center gap-3 border-b border-sidebar-border bg-sidebar px-3 text-sidebar-accent-foreground md:px-4">
      <Link to="/" className="flex min-w-0 shrink-0 items-center gap-2" aria-label={t('app.name')}>
        <BrandLogo tone="light" className="h-6" />
      </Link>
      {/* A hairline between the logo and the shop, so the two read as product and workspace
          rather than one long brand name. */}
      <span aria-hidden="true" className="h-5 w-px shrink-0 bg-sidebar-border" />
      <div className="min-w-0 flex-1 md:flex-none md:max-w-[14rem]">
        <h1 className="truncate text-sm font-medium leading-tight text-sidebar-foreground">
          {/* `||`, not `??`: a session whose shop could not be named carries an empty string,
              and an empty header is worse than the product's own name. */}
          {session?.shopName || t('app.name')}
        </h1>
        {session ? null : (
          <p className="truncate text-xs text-sidebar-foreground">{t('app.tagline')}</p>
        )}
      </div>

      {/*
        One box for the whole shop, on every screen that has a session. Hidden below `sm` by
        the component itself: the phone header keeps the shop name and two icons.
      */}
      {session ? <GlobalSearch /> : null}

      <div className="ml-auto flex shrink-0 items-center gap-1.5">
        {/*
          What you owe, before what you could start: the bell sits left of the green "+" so the
          one primary action on the bar stays the rightmost thing that is not the account.
          Tailors chase no customers, so for them there is nothing behind it.
        */}
        {hasFollowUps ? <FollowUpsMenu /> : null}

        {canCreate ? (
          <DropdownMenu>
            <DropdownMenuTrigger
              aria-label={t('header.create')}
              title={t('header.create')}
              className="flex size-9 items-center justify-center rounded-md bg-sidebar-accent text-sidebar-accent-foreground hover:bg-sidebar-accent/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-accent-foreground/60"
            >
              <Plus aria-hidden="true" className="size-5" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => void navigate('/orders/new')}>
                <ClipboardPlus aria-hidden="true" className="mr-2 size-4" />
                {t('header.createOrder')}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => void navigate('/customers/new')}>
                <UserPlus aria-hidden="true" className="mr-2 size-4" />
                {t('header.createCustomer')}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => void navigate('/appointments')}>
                <CalendarPlus aria-hidden="true" className="mr-2 size-4" />
                {t('header.createAppointment')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}

        {/*
          Help, on every screen, one tap away — being stuck is not a thing somebody navigates
          to, it happens where they are. Icon-only; the label lives in `aria-label`.
        */}
        {session ? (
          <Link
            to="/support"
            aria-label={t('nav.support')}
            title={t('nav.support')}
            className="flex size-9 shrink-0 items-center justify-center rounded-md text-sidebar-foreground hover:bg-sidebar-panel hover:text-sidebar-accent-foreground"
          >
            <LifeBuoy aria-hidden="true" className="size-5" />
          </Link>
        ) : null}
        <UserMenu {...(onSignOut ? { onSignOut } : {})} />
      </div>
    </header>
  );
}
